import React, { useState, useEffect, useRef, useMemo } from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { useTranslation } from "react-i18next";
import { createCollection } from "../utils/pureDataFetch";
import { useUser } from "../contexts/UserContext";
import UpdateCollectionModal from "../components/modal/UpdateCollectionModal";
import DataLoader from "../components/DataLoader";
import MyCollectionListSection from "../components/MyCollectionListSection";
import { useThreadsState } from "../contexts/ThreadsContext";
import { CollectionSummarySection } from "../template/account/CollectionSummarySection";
import { useIsMobile } from "../hooks/useIsMobile";
import { useCollectionInit } from "../hooks/useCollectionInit";
import { useCollections } from "../contexts/ThreadsContext";

// 안정적인 빈 배열 참조
const EMPTY_ARRAY = [];

const MyPage = ({ firstThreadDate }) => {
  const { t } = useTranslation();
  const { profile } = useUser();
  const { lifetimeThreadsList, isLoading } = useThreadsState();
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedThreads, setSelectedThreads] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  
  // 개선된 컬렉션 초기화 훅 사용
  const { 
    collections: myCollections, 
    isLoading: collectionsLoading, 
    error: collectionsError,
    initStatus,
    retry: retryCollections,
    loadCollections: reloadCollections
  } = useCollectionInit();
  
  // 직접적인 컬렉션 로드를 위한 훅
  const { loadCollections: directLoadCollections } = useCollections();
  
  const [sortMetric, setSortMetric] = useState("views");
  const [aggregationType, setAggregationType] = useState("total");
  const isMobile = useIsMobile('xl');





  const handleThreadSelect = (threadId) => {
    setSelectedThreads((prev) =>
      prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId]
    );
  };

  useEffect(() => {
    setCollectionList(() => {
      // lifetimeThreadsList가 없으면 빈 배열 사용
      const threadsList = lifetimeThreadsList || EMPTY_ARRAY;
      return threadsList
        .filter((thread) => selectedThreads.includes(thread.id))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // 오래된 것부터 정렬
    });
  }, [selectedThreads, lifetimeThreadsList]);

  const handleRemoveThread = (threadId) => {
    setSelectedThreads((prev) => prev.filter((id) => id !== threadId));
    setCollectionList((prev) =>
      prev.filter((thread) => thread.id !== threadId)
    );
  };

  const handleCreateCollection = async (collectionData) => {
    try {
      console.log("[MyPage] Creating collection:", collectionData);
      const newCollection = await createCollection(collectionData);
      console.log("[MyPage] Collection created successfully:", newCollection);
      
      // 생성된 컬렉션을 즉시 반영하기 위해 직접 재로드
      const userId = profile?.id || localStorage.getItem("userID");
      if (userId) {
        console.log("[MyPage] Reloading collections for userId:", userId);
        try {
          await directLoadCollections(userId);
          console.log("[MyPage] Collections reloaded successfully");
        } catch (reloadError) {
          console.error("[MyPage] Failed to reload collections:", reloadError);
          // 재로드 실패 시 fallback으로 retry 사용
          setTimeout(() => {
            console.log("[MyPage] Fallback: using retry");
            retryCollections();
          }, 500);
        }
      }
      
      setModalOpen(false);
    } catch (error) {
      console.error("컬렉션 생성 오류:", error);
    }
  };

  // 정렬된 컬렉션을 useMemo로 메모이제이션하여 무한 재렌더링 방지
  const sortedCollections = useMemo(() => {
    if (!myCollections.length) return EMPTY_ARRAY;

    return [...myCollections].sort((a, b) => {
      const getMetricValue = (collection) => {
        // metric 객체에서 직접 값을 가져옴
        const value = collection.metric?.[sortMetric] || 0;

        if (aggregationType === "average") {
          // threadList 배열의 길이로 나누어 평균 계산
          const threadCount = collection.threadList?.length || 1;
          return value / threadCount;
        }
        return value;
      };

      const valueA = getMetricValue(a);
      const valueB = getMetricValue(b);

      return valueB - valueA; // 내림차순 정렬
    });
  }, [myCollections, sortMetric, aggregationType]);

  // 정렬 기준 변경 핸들러 (단순화)
  const handleSortMetricChange = (value) => {
    setSortMetric(value);
  };

  // 집계 방식 변경 핸들러 (단순화)
  const handleAggregationTypeChange = (value) => {
    setAggregationType(value);
  };

  return (
    <PageContainer>
      <DataLoader msg={t("loading.lifetime")} isLoad={isLoading} />
      

      
      {/* 컬렉션 요약 섹션 */}
      {profile && (
        <CollectionSummarySection
          profile={profile}
          collections={myCollections}
          postLoading={isLoading}
          firstThreadDate={firstThreadDate}
          setModalOpen={setModalOpen}
          isMobile={isMobile}
        />
      )}

      {/* 컬렉션 관리 섹션 */}
      {collectionsLoading ? (
        <DataLoader msg={t("loading.collections")} isLoad={true} />
      ) : (
        <MyCollectionListSection
          isMobile={isMobile}
          myCollections={sortedCollections}
          aggregationType={aggregationType}
          sortMetric={sortMetric}
          handleAggregationTypeChange={handleAggregationTypeChange}
          handleSortMetricChange={handleSortMetricChange}
        />
      )}
      <UpdateCollectionModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        threadsList={lifetimeThreadsList || EMPTY_ARRAY}
        selectedThreads={selectedThreads}
        handleThreadSelect={handleThreadSelect}
        isMobile={isMobile}
        onUpdateCollection={handleCreateCollection}
        collectionList={collectionList}
        handleRemoveThread={handleRemoveThread}
        isNew={true}
      />
    </PageContainer>
  );
};

export default MyPage;
